import React, { useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import Animated from 'react-native-reanimated';
import { SVG_COMPONENTS } from '@/assets/admin';
import { PADDING } from '@/constants/residents/homeConstants';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { useAccordionAnimation } from '@/hooks/common/useAccordionAnimation';

interface ExpandableInfoItemProps {
  title: string;
  detail: string;
  isExpanded: boolean;
  onPress: () => void;
}

export const ExpandableInfoItem: React.FC<ExpandableInfoItemProps> = ({ 
  title, 
  detail, 
  isExpanded, 
  onPress 
}) => {
  const styles = useThemedStyles(makeStyles);
  const { colors } = useThemeColors();
  const { setOpen, animatedHeightStyle, animatedChevronStyle, onContentLayout } = useAccordionAnimation({ initiallyOpen: isExpanded });

  useEffect(() => {
    setOpen(isExpanded);
  }, [isExpanded, setOpen]);

  return (
    <View style={styles.container}>
      <TouchableOpacity 
        style={styles.card}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${isExpanded ? 'Expanded' : 'Collapsed'}`}
        accessibilityHint="Double tap to expand or collapse"
        activeOpacity={1}
      >
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          
          <Animated.View style={animatedChevronStyle}>
            <SVG_COMPONENTS.CHEVRON_DOWN
              width={20}
              height={20}
              color={colors.text.muted}
            />
          </Animated.View>
        </View>
        
        <Animated.View style={animatedHeightStyle}>
          <View onLayout={onContentLayout} style={styles.detailInner}>
            <View style={styles.detailContainer}>
              <Text style={styles.detail}>{detail}</Text>
            </View>
          </View>
        </Animated.View>
      </TouchableOpacity>
      
      <View style={styles.divider} />
    </View>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  container: {
    marginBottom: 0,
  },
  divider: {
    height: 1,
    backgroundColor: colors.separator,
    marginHorizontal: PADDING.padding4,
    marginTop: PADDING.padding1,
  },
  card: {
    backgroundColor: colors.bg.base,
    padding: PADDING.padding4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: colors.text.primary,
    marginRight: PADDING.padding2,
  },
  detailInner: {
    position: 'absolute',
    width: '100%',
  },
  detailContainer: {
    marginTop: PADDING.padding1,
    paddingTop: PADDING.padding1,
  },
  detail: {
    fontSize: 14,
    fontWeight: '400',
    color: colors.text.primary,
    lineHeight: 20,
  },
});
